import type { ReactNode } from 'react';
import { CollapsiblePanel } from '@/components/CollapsiblePanel';
import {
  STICKY_SLOT,
  TWO_COLUMN_CLOSED,
  TWO_COLUMN_FILL,
  TWO_COLUMN_FILL_SLOT,
  TWO_COLUMN_COLLAPSE,
  TWO_COLUMN_GRID,
  TWO_COLUMN_SECONDARY_LABEL,
  TWO_COLUMN_STICKY,
  type TwoColumnVariant,
} from '@/lib/twoColumn';
import { cn } from '@/lib/utils';

/**
 * Two variants of one primitive.
 *
 *   detail — narrow left carrying identity and attributes as stacked cards; wide right
 *            carrying the substance: description, related records, history.
 *   form   — the mirror. Wide left for the fields, narrow right for a live summary that
 *            updates as the user types.
 *
 * Both stack to a single column on mobile with the LEFT column first, and both stick
 * their secondary column on desktop. See `lib/twoColumn.ts` for why that is source order
 * rather than an `order-*` utility.
 *
 * ⚠️ [amended UX-FINAL] THE SECONDARY COLUMN NEVER DISAPPEARS AND NEVER SCROLLS SIDEWAYS.
 * Below the split it is a sticky summary bar that opens in place — `CollapsiblePanel`, whose
 * header and `lib/collapse.ts` carry the whole of why it is a class rather than a Sheet, a
 * width branch or a second copy of the DOM. It is ALWAYS wrapped, at every width, so the
 * treatment cannot be forgotten by the next page; `secondaryLabel` only names the bar.
 *
 * ⚠️ THE PRIMITIVE CAPS THE STICKY COLUMN NOW, and the pages no longer do. The threshold is
 * the variant's and a page does not know it — both former callers imported an 880 constant
 * onto a layout that splits at 920. See `TWO_COLUMN_COLLAPSE`.
 *
 * ⚠️ [amended D4.1] `secondaryOpen` MAKES THE SECONDARY A DISCLOSURE, CLOSED AT EVERY WIDTH.
 *
 * Omit it and nothing changes — the column is always there, exactly as `PolicyBuilder` and
 * `ArrangementDetail` have it. Pass a boolean and the host owns a toggle: closed drops the
 * reserved track AND class-hides the slot; open is byte-for-byte the always-on column,
 * `CollapsiblePanel` and all, so below the split it is still the same summary bar.
 *
 * ⚠️ IT IS A STATE BRANCH, NEVER A WIDTH BRANCH — which is the only reason it is allowed to
 * change what renders. `vitest.setup.ts` stubs `matchMedia` to `matches: false` for ever, so
 * a width branch takes the narrow path in every test silently; a boolean the host passes is
 * the same value under test as in a browser.
 *
 * ⚠️ AND CLOSED IS A CLASS, NEVER AN UNMOUNT. The children render exactly once whatever the
 * state, so the ~40 assertions that address the checklist and the order preview by test id
 * keep working without opening anything — jsdom loads no stylesheet, so a Tailwind `hidden`
 * is inert under test and total in a browser. Unmounting deletes them all.
 *
 * ⚠️ TESTING: jsdom has no layout engine, so the stacking itself cannot be asserted here.
 * What CAN be asserted, and is: the slot order in the DOM, and the class map. The rendered
 * result is checked in a browser — see the CHANGELOG's verified-vs-assumed section.
 */
export type TwoColumnLayoutProps = {
  variant: TwoColumnVariant;
  left: ReactNode;
  right: ReactNode;
  /**
   * Omit for the always-on secondary column. Pass a boolean to make it a host-owned
   * disclosure that is CLOSED at every width until the host says otherwise.
   */
  secondaryOpen?: boolean;
  /** Addresses the secondary slot from the host's toggle, for `aria-controls`. */
  secondaryId?: string;
  /** The summary bar's accessible name below the split. Defaults per variant. */
  secondaryLabel?: string;
  /**
   * ⚠️ [C1f §5] THE HOST IS PROMISING THIS GRID A DEFINITE HEIGHT.
   *
   * Omit it and nothing changes — `secondaryOpen`'s shape. Pass `true` and the PRIMARY slot
   * fills the row instead of being sized by its content, so a child inside it can own the
   * overflow. The secondary column is untouched and stays sticky, which is why this stretches
   * one slot rather than the grid.
   *
   * ⚠️ A HOST THAT PASSES IT WITHOUT A BOUNDED ANCESTOR GETS NOTHING WRONG — `h-full` against
   * an auto-height parent simply resolves to the content height, which is today's layout.
   */
  primaryFills?: boolean;
  /** [P7B.1 §2] Passed to the secondary's `CollapsiblePanel` — it closes when this value changes. */
  secondaryCloseOn?: unknown;
  className?: string;
};

export const TwoColumnLayout = ({
  variant,
  left,
  right,
  secondaryOpen,
  secondaryId,
  secondaryLabel,
  primaryFills,
  secondaryCloseOn,
  className,
}: TwoColumnLayoutProps) => {
  const secondary = TWO_COLUMN_STICKY[variant];
  /* `=== false`, not `!secondaryOpen`: undefined means "not a disclosure at all". */
  const closed = secondaryOpen === false;

  /*
   * ⚠️ THE STICKY STAYS ON THE SLOT, NOT ON THE PANEL. A sticky box travels within its
   * containing block, so a panel positioning itself would be confined to a parent exactly
   * its own height and could not move at all — measured, with a control, before this shape
   * was chosen. `lib/collapse.ts` records it.
   */
  const slot = (side: 'left' | 'right', content: ReactNode) =>
    side === secondary ? (
      <div
        data-slot={side}
        {...(secondaryId ? { id: secondaryId } : {})}
        className={cn('min-w-0', STICKY_SLOT[variant], closed && 'hidden')}
      >
        <CollapsiblePanel
          label={secondaryLabel ?? TWO_COLUMN_SECONDARY_LABEL[variant]}
          placement={TWO_COLUMN_COLLAPSE[variant]}
          closeOn={secondaryCloseOn}
        >
          {content}
        </CollapsiblePanel>
      </div>
    ) : (
      <div data-slot={side} className={cn('min-w-0', side !== secondary && primaryFills && TWO_COLUMN_FILL_SLOT)}>
        {content}
      </div>
    );

  return (
    <div
      className={cn(closed ? TWO_COLUMN_CLOSED : TWO_COLUMN_GRID[variant], primaryFills && TWO_COLUMN_FILL, className)}
      data-variant={variant}
      data-secondary={secondaryOpen === undefined ? 'always' : closed ? 'closed' : 'open'}
    >
      {slot('left', left)}
      {slot('right', right)}
    </div>
  );
};
